/**
 * TagChip — web port of client-native/src/ui/molecules/TagChip.tsx.
 *
 * Reusable tag chip with theme support. Used for feed topic selection in both
 * onboarding (dark) and settings (light). px16 py12, radius20 (--radius-chip),
 * border2. Selected = blue border. Active opacity 0.7 (matches RN
 * TouchableOpacity activeOpacity).
 */
import { CSSProperties, FC, ReactNode, useState } from "react";

export interface TagChipProps {
  label: string;
  selected?: boolean;
  theme?: "light" | "dark";
  onPress?: () => void;
  icon?: ReactNode;
}

const TagChip: FC<TagChipProps> = ({
  label,
  selected = false,
  theme = "light",
  onPress,
  icon,
}) => {
  const [active, setActive] = useState(false);
  const isDark = theme === "dark";

  const background = isDark ? "#1a1a1a" : "var(--color-grey-lightest)";
  const borderColor = selected
    ? "var(--color-blue)"
    : isDark
      ? "#333"
      : "var(--color-grey-light)";
  const textColor = isDark ? "#ffffff" : "var(--color-text-primary)";

  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: icon ? 8 : 0,
    minHeight: 44,
    boxSizing: "border-box",
    padding: "12px 16px",
    borderRadius: "var(--radius-chip)",
    border: `2px solid ${borderColor}`,
    backgroundColor: background,
    color: textColor,
    fontSize: "var(--base-font-size)",
    fontWeight: 500,
    lineHeight: 1.4,
    cursor: onPress ? "pointer" : "default",
    opacity: active ? 0.7 : 1,
    transition: "opacity 0.1s ease, border-color 0.15s ease",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
  };

  return (
    <button
      type="button"
      style={style}
      aria-pressed={selected}
      onClick={onPress}
      onPointerDown={() => setActive(true)}
      onPointerUp={() => setActive(false)}
      onPointerLeave={() => setActive(false)}
    >
      {icon}
      {label}
    </button>
  );
};

export default TagChip;
